import type { SelectItem, SelectOption } from "@legal-500/legal500-design-system";
import type { RegionNode } from "@/api/regions.service";

const UNITED_KINGDOM_REGION_SLUG = "r/united-kingdom";

export type LocationRegion = {
  regionName: string | null;
  showRegion: boolean;
};

export function buildLocationRegions(nodes: RegionNode[]): Map<number, LocationRegion> {
  const regionById = new Map(
    nodes.filter(node => node.kind === "region").map(node => [node.id, node]),
  );
  const locationRegions = new Map<number, LocationRegion>();

  for (const node of nodes) {
    if (node.kind !== "country") {
      continue;
    }

    const region = node.parentId === null ? undefined : regionById.get(node.parentId);
    const regionParent =
      region?.parentId === null || region?.parentId === undefined
        ? undefined
        : regionById.get(region.parentId);

    locationRegions.set(node.id, {
      regionName: region?.name ?? null,
      showRegion: regionParent?.slug === UNITED_KINGDOM_REGION_SLUG,
    });
  }

  return locationRegions;
}

export function groupLocationOptions(
  options: SelectOption[],
  locationRegions: Map<number, LocationRegion>,
): SelectItem[] {
  const optionsByRegion = new Map<string, SelectOption[]>();
  const ungrouped: SelectOption[] = [];

  for (const option of options) {
    const regionName = locationRegions.get(Number(option.value))?.regionName;

    if (!regionName) {
      ungrouped.push(option);
      continue;
    }

    optionsByRegion.set(regionName, [...(optionsByRegion.get(regionName) ?? []), option]);
  }

  const groups: SelectItem[] = [];

  for (const [regionName, regionOptions] of optionsByRegion) {
    const [onlyOption] = regionOptions;

    if (regionOptions.length === 1 && onlyOption?.label === regionName) {
      ungrouped.push(onlyOption);
      continue;
    }

    groups.push({
      label: regionName,
      options: [...regionOptions].sort((a, b) => a.label.localeCompare(b.label)),
    });
  }

  const sortedGroups = groups.sort((first, second) => first.label.localeCompare(second.label));

  return [...sortedGroups, ...ungrouped.sort((a, b) => a.label.localeCompare(b.label))];
}
